import Honeybadger from "@honeybadger-io/js"

// Card #4607 -- a failed network request on the Field app (offline, a dead
// zone, a cancelled Turbo visit) throws one of a handful of well-known
// browser TypeErrors. The service worker already serves the cached screen
// and the Tasker sees no effect, so reporting these to Honeybadger is noise,
// not signal -- about 15 taskers, 30-70 times a day.
const EXPECTED_OFFLINE_FETCH_MESSAGES = [
  "Failed to fetch",  // Chrome/Android -- fetch() rejects with this TypeError when the network is unreachable
  "Load failed",      // Safari/iPhone -- the same failure, worded differently
  "Fetch is aborted",  // a visit Turbo or our own watchdog cancelled mid-flight
]

function isExpectedOfflineFetchFailure(notice) {
  const message = notice?.message
  if (typeof message !== "string") return false
  return EXPECTED_OFFLINE_FETCH_MESSAGES.some((known) => message.includes(known))
}

// A DOM event (e.g. a captured tap's PointerEvent) is circular -- it carries
// references back to itself via target/currentTarget/view. Honeybadger's own
// automatic "dom" click breadcrumb is the actual source of the "Converting
// circular structure to JSON" / "cannot serialize cyclic structures" crash
// this card fixes (it stores the raw event, unsanitised, and the very next
// notify() -- for ANY error, related or not -- clones the whole breadcrumb
// store with a plain JSON.stringify and throws). That breadcrumb type is
// turned off below, which is the real fix. This strips any DOM event that
// reaches notice context some other way, belt and braces, so a future notice
// can never reintroduce the same crash.
function stripDomEvents(value, seen = new WeakSet()) {
  if (value === null || typeof value !== "object") return value
  if (typeof Event !== "undefined" && value instanceof Event) return "[DOM Event removed]"
  if (seen.has(value)) return "[Circular]"
  seen.add(value)

  if (Array.isArray(value)) return value.map((item) => stripDomEvents(item, seen))

  const cleaned = {}
  for (const key of Object.keys(value)) {
    cleaned[key] = stripDomEvents(value[key], seen)
  }
  return cleaned
}

const apiKeyMeta = document.querySelector("meta[name='hb-js-key']")
const apiKey = apiKeyMeta?.content

if (apiKey) {
  const railsEnvMeta = document.querySelector("meta[name='rails_env']")

  Honeybadger.configure({
    apiKey,
    environment: railsEnvMeta?.content || "production",
    reportData: railsEnvMeta?.content === "production" || railsEnvMeta?.content === "staging",
    enableUnhandledRejection: true,
    // Card #4607 -- narrowed from `true` (every type). "dom" is the automatic
    // click breadcrumb that stores the raw, circular PointerEvent -- see
    // stripDomEvents above. network/navigation/console stay on: they carry
    // only plain strings and numbers, and are exactly the trail we want for
    // "what was this device doing right before it failed".
    breadcrumbsEnabled: { dom: false, network: true, navigation: true, console: true },
  })

  Honeybadger.beforeNotify((notice) => {
    if (isExpectedOfflineFetchFailure(notice)) return false
    if (notice?.context) notice.context = stripDomEvents(notice.context)
  })
}

export default Honeybadger
